import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

export interface UseTeamFilterOptions {
  initialTeam: string;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: { replace?: boolean }) => void;
}

export interface UseTeamFilterResult {
  selectedTeams: string[];
  availableTeams: string[];
  isLoading: boolean;
  error: Error | null;
  addTeam: (teamName: string) => void;
  removeTeam: (teamName: string) => void;
  setTeams: (teamNames: string[]) => void;
  canRemoveTeam: (teamName: string) => boolean;
  isTeamDisabled: (teamName: string) => boolean;
}

const DISABLED_TEAMS = ['acl-dev-team-frontend'];

function parseTeamsFromUrl(searchParams: URLSearchParams): string[] {
  const teamsParam = searchParams.get('teams');
  if (!teamsParam) return [];
  return teamsParam.split(',').filter(Boolean);
}

function serializeTeamsToUrl(teams: string[]): string {
  return teams.join(',');
}

export function useTeamFilter({
  initialTeam,
  searchParams,
  setSearchParams,
}: UseTeamFilterOptions): UseTeamFilterResult {
  const catalogApi = useApi(catalogApiRef);

  const [selectedTeams, setSelectedTeams] = useState<string[]>(() => {
    if (searchParams) {
      const urlTeams = parseTeamsFromUrl(searchParams);
      if (urlTeams.length > 0) {
        return urlTeams;
      }
    }
    return initialTeam ? [initialTeam] : [];
  });

  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch available teams from catalog
  useEffect(() => {
    let cancelled = false;

    const fetchTeams = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Group',
            'spec.type': 'team',
          },
          fields: ['metadata.name'],
        });

        if (!cancelled) {
          const teamNames = response.items
            .map((entity: Entity) => entity.metadata.name)
            .filter((name: string) => name.startsWith('acl-dev-team-'))
            .sort();
          setAvailableTeams(teamNames);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch teams'));
          setIsLoading(false);
        }
      }
    };

    fetchTeams();

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  // Sync selected teams to URL
  useEffect(() => {
    if (setSearchParams && searchParams) {
      const newParams = new URLSearchParams(searchParams);
      if (selectedTeams.length > 0) {
        newParams.set('teams', serializeTeamsToUrl(selectedTeams));
      } else {
        newParams.delete('teams');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedTeams, searchParams, setSearchParams]);

  const addTeam = useCallback((teamName: string) => {
    setSelectedTeams((prev) => {
      if (prev.includes(teamName)) return prev;
      if (DISABLED_TEAMS.includes(teamName)) return prev;
      return [...prev, teamName];
    });
  }, []);

  const removeTeam = useCallback((teamName: string) => {
    setSelectedTeams((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((t) => t !== teamName);
    });
  }, []);

  const setTeams = useCallback((teamNames: string[]) => {
    const validTeams = teamNames.filter(
      (t) => !DISABLED_TEAMS.includes(t) && t.length > 0
    );
    if (validTeams.length === 0) return;
    setSelectedTeams(validTeams);
  }, []);

  const canRemoveTeam = useCallback(
    (_teamName: string) => {
      return selectedTeams.length > 1;
    },
    [selectedTeams.length]
  );

  const isTeamDisabled = useCallback((teamName: string) => {
    return DISABLED_TEAMS.includes(teamName);
  }, []);

  return {
    selectedTeams,
    availableTeams,
    isLoading,
    error,
    addTeam,
    removeTeam,
    setTeams,
    canRemoveTeam,
    isTeamDisabled,
  };
}

export default useTeamFilter;
